"use client";

import './popover.css';
import { __rest } from '../../_virtual/_tslib.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { useRef, useState, useCallback, useEffect } from 'react';
import calculateRem from '../../utilities/calculate-rem/index.js';
import ScreenReaderText from '../../utilities/screen-reader-text/index.js';
import '../../utilities/use-viewport/index.js';
import styles from './popover.module.scss.js';
import { Scrollable } from '../../scrollable/scrollable/index.js';

const TooltipPopover = (_a) => {
    var { persist = false, onPopoverKeyDown, 'data-testid': testId = 'popover', keepDialogActiveOnMouseEnter, closeDialog, isOsx, content = null, showDialog = false, ariaId = undefined, keyboardFocused = false, toolTipAnchorRef = null, size = 'medium', containerId, tooltipId, hasScrollableContent, customAnchorElement, scrollableId, isFirefox, tooltipWrapperId, setHasScrollableContent, title = null, surface = 'lightPrimary', colorMode = 'light', maxHeight, defaultPopoverPosition = 'above', zIndex = 1 } = _a, rest = __rest(_a, ["persist", "onPopoverKeyDown", 'data-testid', "keepDialogActiveOnMouseEnter", "closeDialog", "isOsx", "content", "showDialog", "ariaId", "keyboardFocused", "toolTipAnchorRef", "size", "containerId", "tooltipId", "hasScrollableContent", "customAnchorElement", "scrollableId", "isFirefox", "tooltipWrapperId", "setHasScrollableContent", "title", "surface", "colorMode", "maxHeight", "defaultPopoverPosition", "zIndex"]);
    const tooltipPopover = useRef(null);
    const tooltipContent = useRef(null);
    const updateCoordinates = useRef(false);
    const [ariaHidden, setAriaHidden] = useState(false);
    const [displayPopoverOnTop, setDisplayPopoverOnTop] = useState(undefined);
    const [popoverHorizontalPosition, setPopoverHorizontalPosition] = useState(0);
    const [popoverCoordinates, setPopoverCoordinates] = useState({
        bottom: 0,
        top: 0,
        width: 0,
        left: 0,
        right: 0,
    });
    const popoverStyles = [
        styles.popover,
        styles[size],
        styles[colorMode],
        styles[surface.toLowerCase()],
    ];
    /**  this is because we need to keep the popover out of view while it calculates its positioning, otherwise it will flash
     * while this is not visible on mouseover events due to the delay, it is on keyboard events.
     */
    if (displayPopoverOnTop === undefined)
        popoverStyles.push(styles.hide);
    if (persist)
        popoverStyles.push(styles.persist);
    if (displayPopoverOnTop)
        popoverStyles.push(styles.displayOnTop);
    if (customAnchorElement)
        popoverStyles.push(styles.customAnchorPresent);
    if (colorMode === 'light')
        popoverStyles.push(styles.colorModeLight);
    if (colorMode === 'dark')
        popoverStyles.push(styles.colorModeDark);
    /* istanbul ignore next @preserve */
    const _positionDialog = () => {
        const position = popoverHorizontalPosition;
        let leftOffset = position;
        const tooltipAnchorPosition = toolTipAnchorRef.getBoundingClientRect();
        if (position > 93)
            leftOffset = 87;
        if (position < -93)
            leftOffset = -87;
        if (customAnchorElement) {
            if (tooltipAnchorPosition &&
                tooltipAnchorPosition.width > popoverCoordinates.width) {
                return `50%`;
            }
            return `calc(50% + ${leftOffset}px)`;
        }
        return `calc(50% + ${leftOffset}px)`; // 106 is each edge - include this a scrollable span
    };
    /* istanbul ignore next @preserve */
    const _positionCaret = () => {
        const position = popoverHorizontalPosition;
        const tooltipAnchorPosition = toolTipAnchorRef.getBoundingClientRect();
        let leftOffset = position;
        if (position > 87)
            leftOffset = 87;
        if (position < -87)
            leftOffset = -87;
        if (customAnchorElement) {
            if (tooltipAnchorPosition &&
                tooltipAnchorPosition.width > popoverCoordinates.width) {
                return `50%`;
            }
            return `calc(50% - ${leftOffset}px)`;
        }
        return `calc(50% - ${leftOffset}px)`; // 106 is each edge - include this a scrollable span
    };
    const positionDialogContentHorizontal = useCallback(() => {
        const gutter = 20;
        const { left: anchorDistanceFromLeft, width: anchorWidth } = toolTipAnchorRef.getBoundingClientRect();
        const anchorDistanceFromRight = window.innerWidth - anchorDistanceFromLeft - anchorWidth + 2;
        const popovercoordinates = tooltipPopover.current.getBoundingClientRect();
        const { width: dialogElementWidth, left: dialogDistaceFromLeft, right: dialogDistanceFromRight, } = popovercoordinates;
        const sizeMap = {
            medium: 13,
            large: 13,
            small: 12,
        };
        let updatedPopoverHorizontalPosition = 0;
        const halfDialogWidth = dialogElementWidth / 2;
        /* istanbul ignore next @preserve */
        const offset = size ? sizeMap[size] : 0;
        const containerNode = containerId &&
            typeof document !== 'undefined' &&
            document.getElementById(containerId);
        if (containerNode) {
            const containerRect = containerNode.getBoundingClientRect();
            const moveLeft = dialogDistanceFromRight + gutter > containerRect.right;
            const moveRight = dialogDistaceFromLeft - gutter < containerRect.left;
            /* istanbul ignore if @preserve */
            if (moveRight) {
                updatedPopoverHorizontalPosition =
                    containerRect.left - dialogDistaceFromLeft + gutter;
            }
            /* istanbul ignore if @preserve */
            if (moveLeft) {
                updatedPopoverHorizontalPosition =
                    containerRect.right - dialogDistanceFromRight - gutter;
            }
            setPopoverHorizontalPosition(updatedPopoverHorizontalPosition);
            return;
        }
        /* istanbul ignore next @preserve */
        if (halfDialogWidth > anchorDistanceFromLeft) {
            // if the anchorElement is <= 20px from left set both the arrow and
            // dialog to current position
            updatedPopoverHorizontalPosition =
                anchorDistanceFromLeft <= gutter
                    ? halfDialogWidth - gutter + offset
                    : halfDialogWidth - anchorDistanceFromLeft + offset;
            setPopoverHorizontalPosition(updatedPopoverHorizontalPosition);
        }
        else if (halfDialogWidth > anchorDistanceFromRight) {
            updatedPopoverHorizontalPosition =
                anchorDistanceFromRight <= gutter
                    ? -(halfDialogWidth - gutter + offset)
                    : -(halfDialogWidth - anchorDistanceFromRight + offset);
            setPopoverHorizontalPosition(updatedPopoverHorizontalPosition);
        }
        else {
            setPopoverHorizontalPosition(0);
        }
    }, [containerId, size, toolTipAnchorRef]);
    const positionPopoverVertical = useCallback(() => {
        const currentAnchorCoordinates = toolTipAnchorRef.getBoundingClientRect();
        /* istanbul ignore next @preserve */
        const tooltipContentWithGutter = tooltipPopover.current
            ? tooltipPopover.current.offsetHeight
            : 0;
        const containerNode = containerId &&
            typeof document !== 'undefined' &&
            document.getElementById(containerId);
        let contentOverflowsTop = false;
        /* istanbul ignore if @preserve */
        if (containerNode) {
            const parentDistanceFromTop = containerNode.getBoundingClientRect().top;
            contentOverflowsTop =
                (currentAnchorCoordinates.top &&
                    currentAnchorCoordinates.top - tooltipContentWithGutter) <
                    parentDistanceFromTop;
        }
        /* istanbul ignore next @preserve */
        const roomForDialogAboveAnchor = currentAnchorCoordinates.top &&
            currentAnchorCoordinates.top > tooltipContentWithGutter;
        /* istanbul ignore next @preserve */
        if (!roomForDialogAboveAnchor ||
            contentOverflowsTop ||
            defaultPopoverPosition === 'below') {
            setDisplayPopoverOnTop(false);
        }
        else if (!displayPopoverOnTop)
            setDisplayPopoverOnTop(true);
    }, [
        containerId,
        defaultPopoverPosition,
        displayPopoverOnTop,
        toolTipAnchorRef,
    ]);
    /* istanbul ignore next @preserve */
    const _positionPopover = () => {
        const coordinates = tooltipPopover.current
            ? tooltipPopover.current.getBoundingClientRect()
            : undefined;
        if (coordinates)
            setPopoverCoordinates(coordinates);
    };
    const _tooltipContentHeight = useCallback(() => {
        // get the height of dialog content, allows scroll bar to render if height is > 212px
        const tooltipContentHeight = tooltipContent.current && tooltipContent.current.offsetHeight - 1;
        // using >= to solve scrollable tooltip content overflow on zoom
        setHasScrollableContent === null || setHasScrollableContent === void 0 ? void 0 : setHasScrollableContent(tooltipPopover.current.offsetHeight - 26 <= tooltipContentHeight);
    }, [setHasScrollableContent]);
    const _trackKeys = useCallback((e) => {
        /* istanbul ignore if @preserve */
        if (e.target.id === `scrollable-content-${scrollableId}` &&
            (e.key === 'Enter' || e.key === ' ')) {
            onPopoverKeyDown === null || onPopoverKeyDown === void 0 ? void 0 : onPopoverKeyDown(e, false);
            return;
        }
        /* close dialog on tab key press */
        if (e.key === 'Tab' || e.key === 'Escape') {
            e.stopPropagation();
            closeDialog === null || closeDialog === void 0 ? void 0 : closeDialog(e);
            return;
        }
        const scrollbarElement = (typeof document !== 'undefined' &&
            document.getElementById(`scrollable-content-${scrollableId}`));
        if (showDialog && scrollbarElement && e.key === 'ArrowDown') {
            e.preventDefault();
            scrollbarElement.scrollTop += 16; // 16 px is the line height of the tooltip content
        }
        if (showDialog && scrollbarElement && e.key === 'ArrowUp') {
            e.preventDefault();
            scrollbarElement.scrollTop -= 16;
        }
    }, [closeDialog, onPopoverKeyDown, scrollableId, showDialog]);
    const getScrollParent = useCallback((node) => {
        if (node == null || node === document.body) {
            return document;
        }
        /* istanbul ignore next @preserve */
        if (node.scrollHeight > node.clientHeight &&
            node !== document.getElementById(tooltipWrapperId)) {
            return node;
        }
        return getScrollParent(node.parentNode);
    }, [tooltipWrapperId]);
    useEffect(() => {
        const delayAriaHidden = setTimeout(() => {
            setAriaHidden(true);
        }, isOsx ? 300 : 10);
        updateCoordinates.current = true;
        const anchoricon = typeof document !== 'undefined'
            ? document.getElementById(tooltipId)
            : null;
        /* istanbul ignore else -- @preserve */
        if (typeof window !== 'undefined') {
            window.addEventListener('scroll', positionDialogContentHorizontal);
            window.addEventListener('scroll', positionPopoverVertical);
            window.addEventListener('resize', positionDialogContentHorizontal);
        }
        if (anchoricon)
            anchoricon.addEventListener('keydown', _trackKeys);
        _positionPopover();
        _tooltipContentHeight();
        positionPopoverVertical();
        if (keyboardFocused) {
            /* istanbul ignore next @preserve */
            setTimeout(() => {
                const scrollContainer = typeof document !== 'undefined'
                    ? document.getElementById(`scrollable-content-${scrollableId}`)
                    : null;
                const scrollTrack = typeof document !== 'undefined'
                    ? document.getElementById(`scrollable-vertical-track-${scrollableId}`)
                    : null;
                // firefox needs aria-describedby to read the scrollable tooltip content
                /* istanbul ignore else */
                if (!customAnchorElement && isFirefox && scrollContainer) {
                    const scrollContainerContent = typeof document !== 'undefined'
                        ? document.querySelectorAll(`#scrollable-content-${scrollableId} span`)
                        : null;
                    /* istanbul ignore else  */
                    if (scrollContainerContent && scrollContainerContent.length > 1) {
                        scrollContainerContent[0].setAttribute('id', 'scrollable-content-header');
                        scrollContainerContent[1].setAttribute('id', 'scrollable-content-content');
                        /* scrollContainer.setAttribute(
                                'aria-describedby',
                                'scrollable-content-header scrollable-content-content'
                              ); */
                    }
                    else {
                        scrollContainerContent === null || scrollContainerContent === void 0 ? void 0 : scrollContainerContent[0].setAttribute('id', 'scrollable-content-content');
                        /* scrollContainer.setAttribute(
                                'aria-describedby',
                                'scrollable-content-content'
                              ); */
                    }
                }
                /* istanbul ignore else */
                if (scrollContainer) {
                    scrollContainer.setAttribute('tabIndex', '0');
                    // scrollContainer.focus();
                    scrollTrack === null || scrollTrack === void 0 ? void 0 : scrollTrack.setAttribute('aria-hidden', 'true');
                    scrollContainer.style.outline = 'none';
                    scrollContainer.style.border = 'none';
                }
            }, 200);
        }
        const parentScrollableContainer = getScrollParent(document.getElementById(tooltipWrapperId));
        /* istanbul ignore if @preserve */
        if (parentScrollableContainer) {
            parentScrollableContainer.addEventListener('scroll', () => {
                if (tooltipPopover.current) {
                    positionPopoverVertical();
                }
            });
        }
        return () => {
            clearTimeout(delayAriaHidden);
            window.removeEventListener('scroll', positionDialogContentHorizontal);
            window.removeEventListener('scroll', positionPopoverVertical);
            window.removeEventListener('resize', positionDialogContentHorizontal);
            /* istanbul ignore else -- @preserve */
            if (anchoricon)
                anchoricon.removeEventListener('keydown', _trackKeys);
            /* istanbul ignore else -- @preserve */
            if (parentScrollableContainer)
                parentScrollableContainer.removeEventListener('scroll', positionPopoverVertical);
        };
    }, [
        _tooltipContentHeight,
        _trackKeys,
        customAnchorElement,
        getScrollParent,
        isFirefox,
        isOsx,
        keyboardFocused,
        positionDialogContentHorizontal,
        positionPopoverVertical,
        scrollableId,
        tooltipId,
        tooltipWrapperId,
    ]);
    /* istanbul ignore next @preserve */
    useEffect(() => {
        if (updateCoordinates.current) {
            _positionPopover();
            positionDialogContentHorizontal();
            // toggle doUpdateCoordinates flag to protect against infinite re-renders
            updateCoordinates.current = false;
        }
        _tooltipContentHeight();
    }, [
        content,
        maxHeight,
        _tooltipContentHeight,
        positionDialogContentHorizontal,
    ]);
    const _calculateMaxHeight = () => {
        const height = typeof maxHeight === 'string' ? parseInt(maxHeight, 10) : maxHeight;
        /* istanbul ignore else */
        if (height) {
            if (height <= 116)
                return 116;
            if (height >= 212)
                return 212;
            return height;
        }
        // default max-height
        return 212;
    };
    const _renderTooltipPopoverWithScroll = () => {
        // 26px refers to overall padding 24px and border 2px
        const scrollHeight = _calculateMaxHeight() - 32;
        return (jsx("span", { className: styles.scrollingPopoverWrapper, children: jsx("span", { className: styles.scrollingContentContainer, id: ariaId, children: jsx(Scrollable, { hitAreaWidth: 4, overflowOrientation: "vertical", minimizeHitArea: false, containerHeight: `${scrollHeight}px`, contentStyle: {
                        paddingRight: calculateRem('8px'),
                    }, surface: colorMode === 'dark' ? 'darkSecondary' : 'lightPrimary', scrollableId: scrollableId, children: jsxs("span", { className: styles.contentWrapper, ref: tooltipContent, children: [isOsx && (jsx(ScreenReaderText, { "aria-hidden": ariaHidden, children: "Expanded." })), title && jsx("span", { className: styles.heading, children: title }), content && jsx("span", { className: styles.content, children: content })] }) }) }) }));
    };
    const _renderTooltipPopoverWithoutScroll = () => (jsx("span", { className: styles.nonScrollingContentContainer, tabIndex: -1, id: ariaId, children: jsxs("span", { className: styles.contentWrapper, ref: tooltipContent, children: [isOsx && (jsx(ScreenReaderText, { "aria-hidden": ariaHidden, children: "Expanded." })), jsxs("span", { className: styles.textWrapper, children: [title && jsx("span", { className: styles.heading, children: title }), content && jsx("span", { className: styles.content, children: content })] })] }) }));
    const customAnchorHeight = customAnchorElement && customAnchorElement.getBoundingClientRect().height;
    return (jsxs("span", Object.assign({ className: popoverStyles.join(' '), onKeyDown: onPopoverKeyDown, "data-testid": testId, onMouseEnter: keepDialogActiveOnMouseEnter, onMouseLeave: persist ? undefined : closeDialog, onClick: (e) => e.stopPropagation(), 
        // eslint-disable-next-line jsx-a11y/aria-role
        role: "text" }, rest, { ref: tooltipPopover, "data-tooltip": ariaId, style: customAnchorElement
            ? {
                '--caret-position': _positionCaret(),
                left: _positionDialog(),
                maxHeight: _calculateMaxHeight(),
                bottom: displayPopoverOnTop
                    ? `${customAnchorHeight + 6}px`
                    : undefined,
                top: !displayPopoverOnTop
                    ? `${customAnchorHeight + 8}px`
                    : undefined,
                zIndex,
            }
            : {
                '--caret-position': _positionCaret(),
                left: _positionDialog(),
                maxHeight: _calculateMaxHeight(),
                zIndex,
            }, children: [hasScrollableContent
                ? _renderTooltipPopoverWithScroll()
                : _renderTooltipPopoverWithoutScroll(), jsx("span", { className: styles.hoverZone })] })));
};

export { TooltipPopover as default };
